import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { NoAvatar } from '../NoAvatar';
import { CoinIcon } from '../../components/CoinIcon';
import { TParticipant } from '../../api/wiz/wiz';

interface PvPCompletedCardProps {
  className?: string;
  prizeAmount: number | null;
  createdAt: string;
  initiator: string;
  opponent: string;
  participants: TParticipant[];
  winner: string | null;
}

export const PvPCompletedCard = ({
  className,
  prizeAmount,
  createdAt,
  initiator,
  opponent,
  participants,
  winner,
}: PvPCompletedCardProps) => {
  const { t } = useTranslation(['translation']);
  const initiatorData = participants.find((p) => p.guid === initiator);
  const opponentData = participants.find((p) => p.guid === opponent);
  const winnerData = participants.find((p) => p.guid === winner);
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    if (diffInSeconds < 60) {
      return t('time.second_ago', { count: diffInSeconds });
    }
    if (diffInMinutes < 60) {
      return t('time.minute_ago', { count: diffInMinutes });
    }
    if (diffInHours < 24) {
      return t('time.hour_ago', { count: diffInHours });
    }
    if (diffInDays < 30) {
      return t('time.day_ago', { count: diffInDays });
    }
    return t('time.month_ago', { count: diffInMonths });
  };

  return (
    <div
      className={classNames(
        'flex items-center justify-between gap-2 border-t border-secondary-grey-500 pt-2',
        className
      )}
    >
      <div className="flex flex-col justify-center font-semibold">
        <h4 className="gap flex items-center text-base text-secondary-grey-900">
          {Number(prizeAmount)?.toFixed(0)}{' '}
          <CoinIcon className="h-3.5 fill-secondary-grey-900" />
        </h4>
        <span className="text-[10px] text-secondary-dark-grey-500">
          {getRelativeTime(createdAt)}
        </span>
      </div>
      <div className="flex items-center">
        {winnerData && (
          <span className="mr-2 flex items-center text-sm font-semibold text-primary-purple-blue-500">
            <span className="max-w-[100px] truncate">{winnerData.username}</span>
            <span className="ml-1">{t('wiz.wins')}</span>
          </span>
        )}
        <div
          className={classNames(
            'rounded-full border-2',
            opponent === winner ? 'border-green-500' : 'border-red-500'
          )}
        >
          <NoAvatar
            className="!h-[32px] !w-[32px] rounded-full"
            src={opponentData?.profile_image ?? undefined}
          />
        </div>
        <div
          className={classNames(
            'relative z-10 -ml-2 rounded-full border-2',
            initiator === winner ? 'border-green-500' : 'border-red-500'
          )}
        >
          <NoAvatar
            className="!h-[32px] !w-[32px] rounded-full"
            src={initiatorData?.profile_image ?? undefined}
          />
        </div>
      </div>
    </div>
  );
};
